import api from "./api";
import {
  classifyVerificationError,
  type VerificationError,
} from "./verificationErrors";
import { gvmBouncerPath, gvmPath } from "./apiPaths";

export { isValidGstinFormat } from "./validators";

const ENDPOINT_NAME = "/gst-details";

export type GstAddress = {
  line1: string;
  line2: string;
  city: string;
  district: string;
  state: string;
  stateCode: string;
  pincode: string;
  country: string;
};

export type GstDetailsResult = {
  valid: boolean;
  status: string | null;
  legalName: string | null;
  tradeName: string | null;
  address: GstAddress | null;
};

export type GstLookupAuthMode = "vendor" | "bouncer";

type RawAddress = Partial<{
  line_1: string;
  line_2: string;
  city: string;
  district: string;
  state: string;
  state_code: string;
  pincode: string;
  country: string;
}>;

type RawResponse = Partial<{
  valid: boolean;
  status: string;
  legal_name: string;
  trade_name: string;
  address: RawAddress;
}> | null;

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strOrNull = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;

export async function lookupGstDetails(
  gstin: string,
  opts: { auth?: GstLookupAuthMode } = {},
): Promise<GstDetailsResult> {
  const auth = opts.auth ?? "vendor";
  const url =
    auth === "vendor" ? gvmPath(ENDPOINT_NAME) : gvmBouncerPath(ENDPOINT_NAME);
  const config = auth === "vendor" ? { useVendorToken: true } : {};
  const res = (await api.post(url, { gstin }, config)) as RawResponse;
  const address = res?.address;
  return {
    valid: Boolean(res?.valid),
    status: strOrNull(res?.status),
    legalName: strOrNull(res?.legal_name),
    tradeName: strOrNull(res?.trade_name),
    address: address
      ? {
          line1: str(address.line_1),
          line2: str(address.line_2),
          city: str(address.city),
          district: str(address.district),
          state: str(address.state),
          stateCode: str(address.state_code),
          pincode: str(address.pincode),
          country: str(address.country),
        }
      : null,
  };
}

export function classifyGstLookupError(err: unknown): VerificationError {
  return classifyVerificationError(err, "gst");
}
